import React from "react";
import { useMutation } from "@tanstack/react-query";
import { Brain, Loader2, Sparkles, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import AIQuotaBadge from "@/components/AIQuotaBadge";

/**
 * AI Analyst panel — v2.5 UX contract:
 *   Chip buttons (discovery) → Answer (analysis) → Evidence (trust) → Follow-up.
 *
 * Every AI response follows the v2.5 AI Response Contract:
 *   executive · evidence · counterpoints · confidence · recommended_investigation.
 *
 * "Alive" feel:
 *   - While waiting, ticks light up sequentially over the known evidence sources
 *   - When answer arrives, executive text types out, other sections stagger in
 */

const CANNED = [
  { key: "why_interesting",     label: "Why is this interesting?" },
  { key: "challenge_consensus", label: "Challenge the consensus" },
  { key: "compare_similar",     label: "Compare similar markets" },
  { key: "assumptions",         label: "What assumptions are traders making?" },
  { key: "invalidators",        label: "What would invalidate this thesis?" },
  { key: "who_disagrees",       label: "Who disagrees?" },
  { key: "investigate_next",    label: "What should I investigate next?" },
];

const EVIDENCE_STEPS = [
  "Loading market context",
  "Consulting consensus",
  "Reviewing tracked traders",
  "Sampling price history",
  "Scanning historical signals",
  "Reasoning from evidence",
];

const CONF_COLOR = {
  High:   "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
  Medium: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  Low:    "text-rose-300 bg-rose-500/15 border-rose-500/30",
};

export default function AIAnalyst({ endpoint, testid = "ai-analyst" }) {
  const [activeKey, setActiveKey] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [typedExec, setTypedExec] = React.useState("");
  const [revealStage, setRevealStage] = React.useState(0);
  const [progressStep, setProgressStep] = React.useState(0);
  const [flashChips, setFlashChips] = React.useState(false);
  const revealTimers = React.useRef([]);
  const progressTimers = React.useRef([]);
  const chipsRef = React.useRef(null);

  const clearRevealTimers = () => {
    revealTimers.current.forEach((t) => clearTimeout(t));
    revealTimers.current = [];
  };
  const clearProgressTimers = () => {
    progressTimers.current.forEach((t) => clearTimeout(t));
    progressTimers.current = [];
  };

  const askMut = useMutation({
    mutationFn: async (prompt_key) =>
      (await api.post(endpoint, { prompt_key })).data,
    onMutate: () => {
      clearRevealTimers();
      setResult(null);
      setTypedExec("");
      setRevealStage(0);
      setProgressStep(0);
    },
    onSuccess: (data, prompt_key) => {
      setActiveKey(prompt_key);
      setResult(data);
      // Typewriter executive text (fast)
      const text = data?.answer?.executive || "";
      const CHARS_PER_TICK = 3;
      const TICK_MS = 15;
      let i = 0;
      const type = () => {
        i = Math.min(text.length, i + CHARS_PER_TICK);
        setTypedExec(text.slice(0, i));
        if (i < text.length) {
          revealTimers.current.push(setTimeout(type, TICK_MS));
        } else {
          // Cascade the remaining sections
          [1, 2, 3, 4].forEach((s, idx) => {
            revealTimers.current.push(setTimeout(() => setRevealStage(s), 220 * (idx + 1)));
          });
        }
      };
      type();
    },
  });

  // Evidence-step progress animation while waiting
  React.useEffect(() => {
    if (!askMut.isPending) return;
    setProgressStep(0);
    let i = 0;
    const step = () => {
      i = Math.min(EVIDENCE_STEPS.length - 1, i + 1);
      setProgressStep(i);
      if (i < EVIDENCE_STEPS.length - 1) {
        progressTimers.current.push(setTimeout(step, 700));
      }
    };
    progressTimers.current.push(setTimeout(step, 400));
    return clearProgressTimers;
  }, [askMut.isPending]);

  React.useEffect(() => () => { clearRevealTimers(); clearProgressTimers(); }, []);

  const ask = (key) => askMut.mutate(key);
  const answer = result?.answer;
  const isPending = askMut.isPending;

  return (
    <section
      className="border border-zinc-800 rounded-md overflow-hidden"
      data-testid={testid}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-zinc-900/60 to-transparent">
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-zinc-300" />
          <h3 className="font-heading font-semibold text-zinc-100 text-lg leading-tight">
            AI Analyst
          </h3>
          <span className="ml-auto flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <AIQuotaBadge endpoint="analyst_answer" compact />
            Claude Sonnet 4.5 · evidence-grounded
          </span>
        </div>
        <div className="text-xs text-zinc-400 mt-1">
          What would you like to understand?
        </div>
      </div>

      {/* Chip buttons */}
      <div ref={chipsRef}
           className={`p-4 border-b border-zinc-800 flex flex-wrap gap-2 transition-all ${
             flashChips ? "bg-zinc-100/5 ring-1 ring-zinc-500/40" : ""
           }`}
           data-testid={`${testid}-prompts`}>
        {CANNED.map((c) => {
          const isActive = activeKey === c.key;
          const disabled = isPending && !isActive;
          return (
            <button
              key={c.key}
              onClick={() => ask(c.key)}
              disabled={isPending}
              data-testid={`${testid}-prompt-${c.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                isActive
                  ? "bg-zinc-100 text-zinc-900 border-zinc-100 shadow-lg"
                  : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800 hover:border-zinc-500"
              } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {isActive && isPending && (
                <Loader2 size={11} className="inline mr-1 animate-spin" />
              )}
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Waiting state — animated evidence-consult ticks */}
      {isPending && !answer && (
        <div className="p-6" data-testid={`${testid}-thinking`}>
          <div className="max-w-md mx-auto">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3 text-center">
              Reasoning from evidence
            </div>
            <ul className="space-y-1.5">
              {EVIDENCE_STEPS.map((s, i) => {
                const done = i < progressStep;
                const active = i === progressStep;
                return (
                  <li key={s}
                      className={`flex items-center gap-2 text-sm transition-all ${
                        done
                          ? "text-emerald-300"
                          : active
                          ? "text-zinc-100"
                          : "text-zinc-600"
                      }`}>
                    <span className="w-3 inline-block">
                      {done ? "✓" : active ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : "·"}
                    </span>
                    <span>{s}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {!answer && !isPending && (
        <div className="px-4 py-8 text-center text-sm text-zinc-500">
          Pick a question above. The analyst will reason only from the stored evidence.
        </div>
      )}

      {answer && (
        <div className="divide-y divide-zinc-800/70" data-testid={`${testid}-answer`}>
          {/* Executive answer — typewriter reveal */}
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Answer
            </div>
            <p className="text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap"
               data-testid={`${testid}-executive`}>
              {typedExec}
              {typedExec.length < answer.executive.length && (
                <span className="inline-block w-1.5 h-4 -mb-0.5 ml-0.5 bg-zinc-300 animate-pulse" />
              )}
            </p>
          </div>

          {/* Evidence */}
          {answer.evidence?.length > 0 && revealStage >= 1 && (
            <div className="p-4 fade-in-up">
              <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/70 mb-2">
                Evidence
              </div>
              <ul className="space-y-1.5" data-testid={`${testid}-evidence`}>
                {answer.evidence.map((e, i) => (
                  <li key={i} className="flex gap-2 text-sm text-zinc-300 leading-snug">
                    <span className="text-emerald-400 mt-0.5">✓</span>
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Counterpoints */}
          {answer.counterpoints?.length > 0 && revealStage >= 2 && (
            <div className="p-4 fade-in-up">
              <div className="text-[10px] uppercase tracking-[0.25em] text-rose-300/70 mb-2">
                Counterpoints
              </div>
              <ul className="space-y-1.5" data-testid={`${testid}-counterpoints`}>
                {answer.counterpoints.map((e, i) => (
                  <li key={i} className="flex gap-2 text-sm text-zinc-300 leading-snug">
                    <span className="text-rose-400 mt-0.5">×</span>
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Confidence + Recommended Investigation */}
          {revealStage >= 3 && (
            <div className="p-4 grid md:grid-cols-2 gap-4 fade-in-up">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  Thesis confidence
                </div>
                <span
                  data-testid={`${testid}-confidence`}
                  className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-heading ${
                    CONF_COLOR[answer.confidence] || CONF_COLOR.Medium
                  }`}
                >
                  {answer.confidence}
                </span>
                <div className="text-[10px] text-zinc-500 mt-2 leading-snug max-w-xs">
                  How strongly the AI thinks the underlying evidence supports the
                  thesis — distinct from the signal quality (data completeness)
                  shown at the top of this page.
                </div>
                {answer.divergence && (
                  <div className="mt-3 border border-amber-500/40 bg-amber-500/10 rounded-md p-2 text-[11px] text-amber-100 leading-snug"
                       data-testid={`${testid}-divergence`}>
                    <div className="font-heading text-amber-200 text-[10px] uppercase tracking-widest mb-1">
                      ⚠ Signal quality vs. thesis diverges
                    </div>
                    {answer.divergence.message}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-blue-300/70 mb-2">
                  Recommended Investigation
                </div>
                <ul className="space-y-1.5"
                    data-testid={`${testid}-recommended`}>
                  {(answer.recommended_investigation || []).map((r, i) => (
                    <li key={i} className="flex gap-2 text-sm text-zinc-300 leading-snug">
                      <ChevronRight size={14} className="text-blue-400 mt-0.5 shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Evidence-used footer */}
          {revealStage >= 4 && result.evidence_used?.length > 0 && (
            <div className="px-4 py-3 bg-zinc-900/40 flex flex-wrap gap-1.5 fade-in-up">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-2">
                Evidence sources:
              </span>
              {result.evidence_used.map((label, i) => (
                <span key={i}
                      className="text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-800 text-zinc-300 border border-zinc-700">
                  {label}
                </span>
              ))}
            </div>
          )}

          {/* Follow-up */}
          {revealStage >= 4 && (
            <div className="p-4 flex items-center justify-between text-[10px] text-zinc-500 font-mono-num fade-in-up">
              <span>{new Date(result.generated_at).toLocaleString()}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  clearRevealTimers();
                  setActiveKey(null);
                  setResult(null);
                  setTypedExec("");
                  setRevealStage(0);
                  // Scroll to the chip row and give it a brief pulse so the user
                  // sees where the next question goes.
                  requestAnimationFrame(() => {
                    chipsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    setFlashChips(true);
                    setTimeout(() => setFlashChips(false), 1200);
                  });
                }}
                data-testid={`${testid}-followup`}
                className="h-7 text-xs bg-zinc-900 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              >
                <Sparkles size={11} className="mr-1" />
                Ask another question
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
